<script setup>
import { FlexRender } from '@tanstack/vue-table';
import SortButton from './SortButton.vue';
import { computed } from 'vue';

const props = defineProps({
  table: {
    type: Object,
    required: true,
  },
  fixed: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'relaxed',
  },
  borderless: {
    type: Boolean,
    default: false,
  },
  // Minimal variant: reduce borders/lines/backgrounds. Use for reports only.
  minimal: {
    type: Boolean,
    default: false,
  },
  rowClickable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['rowClick']);

const isRelaxed = computed(() => props.type === 'relaxed');
const headerClass = computed(() =>
  isRelaxed.value
    ? 'first:rounded-bl-lg first:rounded-tl-lg last:rounded-br-lg last:rounded-tr-lg'
    : ''
);
</script>

<template>
  <div
    :class="[
      'rounded-lg setting-table-container',
      props.minimal ? 'bg-transparent' : '',
      { 'border border-n-gray-1 dark:border-n-vars-black-bd/80': !borderless && !props.minimal }
    ]"
  >
    <table
      :class="[
        'w-full',
        { 'table-fixed': fixed }
      ]"
    >
      <thead :class="props.minimal ? '' : 'sticky top-0'">
        <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id" class="h-[50px] !bg-n-gray-1 dark:!bg-n-vars-black-br/30">
          <th
            v-for="header in headerGroup.headers"
            :key="header.id"
            :style="{
              width: `${header.getSize()}px`,
            }"
            class="text-left px-3  !text-sm !font-semibold !text-n-gray-7 dark:!text-n-gray-3 whitespace-nowrap"
            :class="[props.minimal ? 'py-2' : 'py-3', headerClass]"
            @click="header.column.getCanSort() && header.column.toggleSorting()"
          >
            <div v-if="!header.isPlaceholder" class="flex place-items-center gap-1">
              <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              <SortButton v-if="header.column.getCanSort()" :header="header" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody :class="props.minimal ? '' : ''">
        <tr
          v-for="row in table.getRowModel().rows"
          :key="row.id"
          :class="props.rowClickable ? 'hover:bg-n-gray-2/40 cursor-pointer' : ''"
          @click="props.rowClickable && emit('rowClick', row.original)"
        >
          <td v-for="cell in row.getVisibleCells()" :key="cell.id" class="px-3" :class="props.minimal ? 'py-2' : 'py-3'">
            <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
